import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';

import postAddPost from '../../../../api/graphql/mutations/postAddPost';
import postEditPost from '../../../../api/graphql/mutations/postEditPost';
import getPostsByType from '../../../../api/graphql/queries/getPostsByType';
import checkPostSlugNameRepeated from '../../../../utils/checkPostSlugNameRepeated';
import Notification, { useNotification } from '../../../Notification';

import slugify from '../../../../utils/slugify';

const SLUGNAME_INPUT = 'slugName';

const GROUP_STYLES = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const setValue = dispatch => (name, value) => dispatch({ name, value });
const setSlugNameValue = dispatch => value => dispatch({ type: SLUGNAME_INPUT, value });

const reducer = (state, action) => {
  switch (action.type) {
    case SLUGNAME_INPUT: return ({
      ...state,
      slugName: slugify(action.value),
      postName: action.value,
    });
    default: return ({ ...state, [action.name]: action.value });
  }
};

// TODO: move this and its deps to its own file
export const usePostCategories = (category = '') => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [currentCategory, setCurrentCategory] = useState(category);

  useEffect(() => {
    getPostsByType(undefined, ['section', 'sub-section'])
      .then(({ posts }) => {
        if (!posts) return;
        const allCategories = posts.filter(post => post.slugName.indexOf('pc') >= 0).map(post => ({ label: post.postName, value: post.postName }));
        const allSubCategories = posts.filter(post => post.slugName.indexOf('psc') >= 0).reduce((acc, curr) => {
          const option = { label: slugify(curr.postName), value: slugify(curr.postName) };
          acc[curr.postCategory[0]] = acc[curr.postCategory[0]] ? [...acc[curr.postCategory[0]], option] : [option];
          return acc;
        }, {});
        setCategories(allCategories);
        setSubcategories(allSubCategories);
      })
      .catch(err => console.log(err));
  }, []);
  return { categories, subcategories: subcategories[currentCategory], setCurrentCategory };
};

export const transformToSelectData = (options) => {
  if (!Array.isArray(options)) return [];
  return options.map(option => ({ label: option, value: option }));
};

export const formatGroupLabel = data => (
  <div style={GROUP_STYLES}>
    <span>{data.label}</span>
  </div>
);

const ReusableComponentConfig = ({ post, addStatusMessage, closeModal, isEdit, updatePost }) => {
  const [state, dispatch] = useReducer(reducer, post);
  const { postCategory: category, postSubCategory } = useParams();
  const history = useHistory();
  const { categories, subcategories, setCurrentCategory } = usePostCategories(category);
  const [status, handleStatus] = useNotification();

  const { postName, slugName, postCategory, components } = state;

  const mainCategory = postCategory[0] || 'blank';

  useEffect(() => {
    if (isEdit) return;

    const postCategories = [category];
    if (postSubCategory) postCategories.push(postSubCategory);
    setValue(dispatch)('postCategory', postCategories);
  }, []);

  useEffect(() => {
    setCurrentCategory(mainCategory);
  }, [mainCategory]);

  const handleSlugName = (e) => {
    const { value } = e.target;
    setSlugNameValue(dispatch)(value);
  };

  const handleSelect = (option) => {
    const options = option.map(opt => opt.value);
    setValue(dispatch)('postCategory', options);
  };

  const groupedOptions = [
    {
      label: 'Main Category',
      options: categories,
    },
    {
      label: 'Secondary Categories',
      options: subcategories || [],
    },
  ];

  const editPost = () => {
    postEditPost(state)
      .then((res) => {
        const { isEdited = false } = res;
        if (isEdited) {
          closeModal();
          addStatusMessage('icon-checkbox', 'Post updated', 'green');
          updatePost(state);
        } else {
          handleStatus({ status: 'error', text: 'Something happened, component was not edited', color: 'red' });
        }
      })
      .catch(err => console.log(err));
  };

  const sendPost = async () => {
    if (!slugName || postCategory.length === 0) return;
    const slugNameRepeated = await checkPostSlugNameRepeated(slugName);
    if (slugNameRepeated) {
      handleStatus({ status: 'error', text: 'SlugName already taken', color: 'red' });
      return;
    }

    const newPost = {
      ...state,
      components: String(JSON.stringify(components)),
    };

    postAddPost(newPost)
      .then(({ isAdded, slugName: createdPostSlugName }) => {
        if (isAdded) history.push(`/cms/structure/${mainCategory}/${createdPostSlugName}`);
        else handleStatus({ status: 'error', text: 'Something happened, component was not created', color: 'red' });
      })
      .catch(err => console.log(err));
  };

  return (
    <div className="post-config">
      <div className="bg-greyFilter px-4 flex justify-between items-center rounded-t-lg px-7 h-12">
        <span className="font-americaMonoBold text-sm uppercase text-black">Basic Component Info</span>
      </div>
      <div className="bg-black text-white p-7 py-5 flex flex-wrap w-full rounded-b-lg">
        <Notification {...{ ...status }} />
        <div className="flex w-full">
          {isEdit ? (
            <span className="font-americaMonoRegular text-xs block my-2 w-1/2 mr-2 self-center">
              Slugname:
              <span className="lowercase ml-2">{slugName}</span>
            </span>
          ) : (
            <label className="font-americaMonoRegular text-xs my-2 w-48% mr-6" htmlFor="slugName">
              Component Name
              <input className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-4 rounded-full" onChange={handleSlugName} name="slugName" type="text" value={postName} placeholder="Slugname for this post" />
              <span className="font-americaMonoRegular text-xs p-2 pl-0 block">
                SlugName:
                <span className="lowercase ml-2">{slugName}</span>
              </span>
            </label>
          )}
        </div>
        <div className="flex flex-col justify-end w-full mb-6">
          <span className="font-americaMonoRegular text-xs block my-2 w-1/2 mr-2">
            Select Component Category:
          </span>
          <Select
            className="font-americaMonoRegular text-black font-bold mt-1"
            name="postCategory"
            options={groupedOptions}
            isMulti
            value={transformToSelectData(postCategory)}
            onChange={handleSelect}
            formatGroupLabel={formatGroupLabel}
          />
        </div>
        <div className="flex justify-end w-full">
          <button className={`font-americaMonoBold text-xs px-6 py-2 rounded-full ${slugName ? 'text-black' : 'text-grey'} uppercase bg-greyFilter mb-0`} onClick={isEdit ? editPost : sendPost} type="button" disabled={!slugName}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

ReusableComponentConfig.propTypes = {
  post: PropTypes.shape({}).isRequired,
  addStatusMessage: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  updatePost: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
};

export default ReusableComponentConfig;
